// Take-upload copy — the "Upload file" flow on the recording page.
//
// Professional voice actors often record in their own DAW and deliver a
// finished WAV instead of performing into the browser. This module covers
// the trigger button, the staged progress labels, the advisory mismatch
// notes (we accept the file anyway — server QC has the final word), and the
// few hard errors.
//
// Tone rules apply (see ./types.ts): first person, always give the reason,
// one issue at a time. Mismatch notes are neutral/informative — never red,
// never blaming. Red is reserved for true failures (not a WAV, upload broke).
// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
export const BUTTON = {
    upload_file: {
        icon: '⬆️',
        en: 'Upload file',
        es: 'Subir archivo',
        zh: '上传文件',
    },
};
export const STATUS = {
    parsing: {
        en: 'Reading your WAV…',
        es: 'Leyendo su WAV…',
        zh: '正在读取 WAV…',
    },
    hashing: {
        en: 'Fingerprinting the audio…',
        es: 'Generando la huella del audio…',
        zh: '正在计算音频校验值…',
    },
    uploading: {
        en: 'Uploading your take…',
        es: 'Subiendo su toma…',
        zh: '正在上传这条 take…',
    },
    finalizing: {
        en: 'Filing it with this cue…',
        es: 'Registrándola en esta línea…',
        zh: '正在归档到当前台词…',
    },
    done: {
        icon: '✅',
        en: 'Take uploaded — it is in your take history now.',
        es: 'Toma subida — ya está en su historial de tomas.',
        zh: 'Take 已上传,可以在 take 历史里看到了。',
    },
    failed: {
        en: 'That upload did not finish.',
        es: 'Esa subida no llegó a completarse.',
        zh: '这次上传没有完成。',
    },
};
/**
 * Build the advisory note for a WAV-spec mismatch. `detail` is the parsed
 * value as rendered by wav-parse (e.g. "44100 Hz", "32-bit", "2 channels",
 * "format tag 85").
 */
export const WARNINGS = {
    sample_rate: (detail) => ({
        icon: 'ℹ️',
        en: `This file is ${detail} — the project targets 48 kHz. We will still take it; QC may flag timing.`,
        es: `Este archivo está a ${detail} — el proyecto trabaja a 48 kHz. Lo aceptamos igualmente; el control de calidad podría marcar el timing.`,
        zh: `这个文件是 ${detail},项目目标是 48 kHz。我们照样收下,但 QC 可能会提示时长偏差。`,
    }),
    bit_depth: (detail) => ({
        icon: 'ℹ️',
        en: `This file is ${detail} — the project targets 24-bit. We will still take it; you may lose a little headroom.`,
        es: `Este archivo es de ${detail} — el proyecto trabaja a 24 bits. Lo aceptamos igualmente; podría perder algo de margen dinámico.`,
        zh: `这个文件是 ${detail},项目目标是 24-bit。我们照样收下,只是动态余量可能略少。`,
    }),
    channels: (detail) => ({
        icon: 'ℹ️',
        en: `This file has ${detail} — the project records in mono. We will still take it and fold it down if needed.`,
        es: `Este archivo tiene ${detail} — el proyecto graba en mono. Lo aceptamos igualmente y lo mezclaremos a mono si hace falta.`,
        zh: `这个文件是 ${detail},项目按单声道收音。我们照样收下,需要时会做单声道合并。`,
    }),
    format: (detail) => ({
        icon: 'ℹ️',
        en: `This WAV uses an unusual encoding (${detail}) — the project targets PCM. We will still take it; QC will tell us if it cannot be read.`,
        es: `Este WAV usa una codificación poco común (${detail}) — el proyecto trabaja en PCM. Lo aceptamos igualmente; el control de calidad avisará si no se puede leer.`,
        zh: `这个 WAV 用了少见的编码(${detail}),项目目标是 PCM。我们照样收下,QC 如果读不了会提示。`,
    }),
};
export const ERRORS = {
    not_wav: {
        icon: '🎵',
        en: 'I could not read that as a WAV. Export this take as .wav from your DAW (48 kHz / 24-bit works best) and try again.',
        es: 'No pude leer ese archivo como WAV. Exporte esta toma como .wav desde su DAW (48 kHz / 24 bits es lo ideal) e inténtelo de nuevo.',
        zh: '这个文件我读不出 WAV 格式。请从你的 DAW 导出 .wav(推荐 48 kHz / 24-bit)再试一次。',
    },
    too_large: {
        icon: '💾',
        en: 'That file is over 200 MB — much bigger than a single take should be. Export just this cue and try again.',
        es: 'Ese archivo supera los 200 MB — mucho más de lo que debería ocupar una sola toma. Exporte solo esta línea e inténtelo de nuevo.',
        zh: '这个文件超过 200 MB,远大于单条 take 的正常体积。请只导出这一条台词再试。',
    },
    upload_failed: {
        icon: '📴',
        en: 'The upload did not go through — your file is safe on your disk. Check your connection and try again.',
        es: 'La subida no se completó — su archivo sigue intacto en su disco. Revise su conexión e inténtelo de nuevo.',
        zh: '上传没有成功,你电脑里的文件不受影响。检查一下网络后再试一次。',
    },
};
// ---------------------------------------------------------------------------
// BATCH — the batch-upload page (one whole recording batch delivered as
// many WAVs at once). Mapping notes follow the tone rules: conflicts and
// missing mappings are neutral asks, never red; red stays reserved for the
// per-file hard failures above.
// ---------------------------------------------------------------------------
export const BATCH = {
    TAB: {
        en: 'Upload',
        es: 'Subir',
        zh: '上传',
    },
    TITLE: {
        icon: '📦',
        en: 'Batch upload',
        es: 'Subida por lotes',
        zh: '批量上传',
    },
    SUBTITLE: {
        en: 'Recorded the whole batch in your own studio? Drop every WAV here — I will match each file to its cue from the filename, and you can adjust any mapping before the upload starts.',
        es: '¿Grabó el lote completo en su propio estudio? Arrastre aquí todos los WAV — emparejaré cada archivo con su línea a partir del nombre, y usted puede ajustar cualquier asignación antes de iniciar la subida.',
        zh: '在自己的棚里把整批都录完了?把所有 WAV 拖进来,我会按文件名把每个文件对应到台词编号,开始上传前你可以随时手动调整。',
    },
    BATCH_LABEL: {
        en: 'Batch',
        es: 'Lote',
        zh: '批次',
    },
    batchOption: (n) => ({
        en: `Batch ${n}`,
        es: `Lote ${n}`,
        zh: `第 ${n} 批`,
    }),
    DROPZONE: {
        icon: '🎚️',
        en: 'Drop your WAV files here, or click to choose them',
        es: 'Arrastre sus archivos WAV aquí, o haga clic para elegirlos',
        zh: '把 WAV 文件拖到这里,或点击选择文件',
    },
    DROPZONE_HINT: {
        en: 'I read the cue number from each filename (e.g. "nora_cue042.wav" → cue 42). Files without a number fill the next open cues in order.',
        es: 'Leo el número de línea del nombre de cada archivo (p. ej. "nora_cue042.wav" → línea 42). Los archivos sin número ocupan en orden las siguientes líneas libres.',
        zh: '我会从文件名里识别台词编号(例如 "nora_cue042.wav" → 第 42 句);没有编号的文件会按顺序填入还没占用的台词。',
    },
    COLUMNS: {
        file: {
            en: 'File',
            es: 'Archivo',
            zh: '文件',
        },
        audio: {
            en: 'Audio',
            es: 'Audio',
            zh: '音频',
        },
        cue: {
            en: 'Cue',
            es: 'Línea',
            zh: '台词',
        },
        status: {
            en: 'Status',
            es: 'Estado',
            zh: '状态',
        },
    },
    CUE_PLACEHOLDER: {
        en: 'Choose a cue…',
        es: 'Elija una línea…',
        zh: '选择台词…',
    },
    READY: {
        en: 'Ready',
        es: 'Listo',
        zh: '就绪',
    },
    UNMAPPED: {
        icon: 'ℹ️',
        en: 'I could not find a cue number in this filename — please pick the cue yourself.',
        es: 'No encontré un número de línea en este nombre de archivo — elija usted la línea, por favor.',
        zh: '文件名里没找到台词编号,请手动选一下对应的台词。',
    },
    CONFLICT: {
        icon: 'ℹ️',
        en: 'Two files point at this cue — please move one of them to another cue.',
        es: 'Dos archivos apuntan a esta línea — mueva uno de ellos a otra línea, por favor.',
        zh: '有两个文件都对到了这一句,请把其中一个换到别的台词。',
    },
    startUpload: (n) => ({
        icon: '⬆️',
        en: `Start upload (${n} ${n === 1 ? 'file' : 'files'})`,
        es: `Iniciar subida (${n} ${n === 1 ? 'archivo' : 'archivos'})`,
        zh: `开始上传(${n} 个文件)`,
    }),
    uploadingProgress: (done, total) => ({
        en: `Uploading ${done} of ${total}…`,
        es: `Subiendo ${done} de ${total}…`,
        zh: `正在上传 ${done}/${total}…`,
    }),
    summaryAllOk: (n) => ({
        icon: '✅',
        en: `All ${n} ${n === 1 ? 'file' : 'files'} uploaded — they are in your take history now.`,
        es: `${n === 1 ? 'El archivo se subió' : `Los ${n} archivos se subieron`} — ya están en su historial de tomas.`,
        zh: `${n} 个文件全部上传完成,已经进入你的 take 历史。`,
    }),
    summaryMixed: (ok, failed) => ({
        en: `${ok} uploaded, ${failed} did not make it. Your files are safe on your disk — retry the marked rows below.`,
        es: `${ok} subidos, ${failed} no se completaron. Sus archivos siguen intactos en su disco — reintente las filas marcadas abajo.`,
        zh: `${ok} 个上传成功,${failed} 个没有完成。你电脑里的文件不受影响,可以在下面对标记的行单独重试。`,
    }),
    RETRY: {
        en: 'Retry',
        es: 'Reintentar',
        zh: '重试',
    },
    REMOVE: {
        en: 'Remove',
        es: 'Quitar',
        zh: '移除',
    },
    NO_CUES: {
        en: 'This batch has no cues assigned to you yet.',
        es: 'Este lote todavía no tiene líneas asignadas a usted.',
        zh: '这一批里还没有分配给你的台词。',
    },
    LOADING_CUES: {
        en: 'Loading your cues…',
        es: 'Cargando sus líneas…',
        zh: '正在加载你的台词…',
    },
};
